/* eslint-disable react/display-name */
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import AlertDialog from '../components/AlertDialog';
import { TextInput, Checkbox } from '../components/Form';
import { FormErrorBase } from '../components/Formik/components/FormError';

const promptable =
  Component =>
  ({ dispose, reject, resolve, title, options = {} }) => {
    const [isOpen, setIsOpen] = useState(true);
    const [value, setValue] = useState(options.defaultValue || '');
    const [error, setError] = useState('');
    const [checkboxChecked, setCheckboxChecked] = useState(
      options.checkbox?.defaultChecked ?? false,
    );

    const onDismiss = () => {
      setIsOpen(false);
      dispose();
    };

    const onCancel = () => {
      setIsOpen(false);
      reject(null);
    };

    const onProceed = () => {
      if (options.validate) {
        const validationError = options.validate(value);
        if (validationError) {
          setError(validationError);
          return;
        }
      }
      setIsOpen(false);
      // If checkbox option is provided, return object with both values
      if (options.checkbox) {
        resolve({ value, [options.checkbox.name || 'checked']: checkboxChecked });
      } else {
        resolve(value);
      }
    };

    const handleChange = event => {
      setValue(event.target.value);
      if (error) setError('');
    };

    return (
      <Component
        title={title}
        options={{
          ...options,
          confirm: options.confirmText || 'Confirm',
          cancel: options.cancelText || 'Cancel',
        }}
        onCancel={onCancel}
        onDismiss={onDismiss}
        onProceed={onProceed}
        isOpen={isOpen}
        customContent={
          <div className="space-y-4">
            <TextInput
              autoFocus
              fullWidth
              value={value}
              onChange={handleChange}
              placeholder={options.placeholder}
            />
            {error && <FormErrorBase>{error}</FormErrorBase>}
            {options.checkbox && (
              <Checkbox
                id="prompt-checkbox"
                label={options.checkbox.label}
                checked={checkboxChecked}
                onChange={e => setCheckboxChecked(e.target.checked)}
              />
            )}
          </div>
        }
      />
    );
  };

const createPrompt = (Component, mountingNode) => {
  return props => {
    const wrapper = (mountingNode || document.body).appendChild(document.createElement('div'));
    const root = createRoot(wrapper);

    const promise = new Promise((resolve, reject) => {
      try {
        root.render(<Component reject={reject} resolve={resolve} dispose={dispose} {...props} />);
      } catch (e) {
        console.error(e);
        throw e;
      }
    });

    function dispose() {
      root.unmount();
      setTimeout(() => {
        if (wrapper && wrapper.parentNode) {
          wrapper.parentNode.removeChild(wrapper);
        }
      });
    }

    return promise.then(
      result => {
        dispose();
        return result;
      },
      result => {
        dispose();
        return Promise.reject(result);
      },
    );
  };
};

const promptableDialog = promptable(AlertDialog);
const promptWrapper = createPrompt(promptableDialog);

// Wrapper function for easy usage
export function prompt(title, options = {}) {
  return promptWrapper({ title, options });
}
